import TierFilter from '../../tiers/layouts/tier-filter';
import { boardLink } from '../utils/board-navigation';
import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Gate } from '../../../global/contexts/auth/auth';
import AuthorizationBadge from './authorization-badge';
import AuthorizeTask from '../../breakdown/layouts/authorize-task';
import { UNAUTHORIZED, authorizationOf, canBeAuthorized } from '../utils/authorization';
import { useAuthorNames, useRepositoryInfo } from '../../../global/contexts/lookups/lookups';
import ProjectFilter from '../../projects/layouts/project-filter';
import RepositoryFilter from '../../repositories/layouts/repository-filter';
import KindBadge from '../../repositories/layouts/kind-badge';
import { useTiersList } from '../../tiers/contexts/tiers';
import { TASKS_PAGE_SIZE, useTasksList } from '../contexts/tasks';
import { IconEdit } from '../../../common/icons';
import DeactivateButton from '../../../common/components/deactivate/deactivate-button';
import { tierKeys } from '../../tiers/queries/keys';
import { taskKeys } from '../queries/keys';
import { taskPaths } from '../queries/paths';
import {
    TASK_KIND_OPTIONS,
    WORKFLOW_STATE_LABEL,
    WORKFLOW_STATE_OPTIONS,
} from '../utils/form';

import '../styles/overview.css';

// Rows. A task list is read by scanning a column — which are ready, which are
// claimed, which are blocked — and that is what rows are for.

const KIND_LABEL = Object.fromEntries(
    TASK_KIND_OPTIONS.map(option => [option.value, option.label]),
);

const TIER_LOOKUP_LIMIT = 100;

function TaskRow({ task, tierName, assignedName, repo }) {
    const [params] = useSearchParams();
    return (
        <li className={`task-row${authorizationOf(task.workflow_state) === UNAUTHORIZED ? ' task-row--unauthorized' : ''}`}>
            <span className={`task-row__kind task-row__kind--${task.kind}`}>
                {KIND_LABEL[task.kind] ?? task.kind}
            </span>

            <span className="task-row__identity">
                {/* The title opens the READ view. It used to be a link labelled
                    "Editar" behind `tasks:write`, so somebody who could only
                    read the work had no way into a row at all. */}
                <Link to={boardLink(`/admin/tasks/${task.id}`, params)} className="task-row__title">
                    {task.title}
                </Link>
                <span className="task-row__tier">
                    {/* A one-off task has no tier: «Tier #null» was the old answer. */}
                    {task.modality === 'one_off'
                        ? <span className="task-row__oneoff">Puntual · sin tier</span>
                        : (tierName || `Tier #${task.tier_id}`)}
                    {/* El repositorio, por su forma y su tono: se reconoce de un
                        vistazo sin gastar una columna en su nombre. */}
                    {repo && <KindBadge kind={repo.kind} label={false} />}
                    <AuthorizationBadge workflowState={task.workflow_state} />
                </span>
            </span>

            <span className={`task-state task-state--${task.workflow_state}`}>
                {WORKFLOW_STATE_LABEL[task.workflow_state] ?? task.workflow_state}
            </span>

            {/* Whose work it is — a different question from who has it reserved
                right now, and one this list could not answer before T03. */}
            <span className="task-row__assigned" title="Responsable asignado">
                {assignedName ?? <span className="task-row__unassigned">Sin asignar</span>}
            </span>

            {/* «Peso», not «Prioridad»: higher first WITHIN ITS GROUP, and
                assigned work goes before the general queue. */}
            <span className="task-row__priority" title="Peso de prioridad: mayor primero dentro de su grupo">
                {task.priority ?? 0}
            </span>

            <span className="row-actions">
                {/* A proposal is authorized from the row, in a modal: the same
                    one the task's screen opens, without leaving the list. */}
                {canBeAuthorized(task) && (
                    <Gate domain="tasks:authorize">
                        <AuthorizeTask
                            task={task}
                            tierId={task.tier_id}
                            iconOnly
                            className="task-row__authorize"
                        />
                    </Gate>
                )}
                <Gate domain="tasks:write">
                    <Link
                        to={`/admin/tasks/${task.id}/edit`}
                        className="task-row__edit"
                        aria-label={`Editar ${task.title}`}
                        title="Editar"
                    >
                        <IconEdit />
                    </Link>
                </Gate>
                {/* A task counts in its tier's progress: the tiers are read again. */}
                <Gate domain="tasks:admin">
                    <DeactivateButton
                        name={task.title}
                        noun="tarea"
                        path={taskPaths.detail(task.id)}
                        invalidate={[taskKeys.all, tierKeys.all]}
                        consequence="Queda inactiva y sale del tablero y de la cola. La fila se conserva, con toda su historia: no se borra nada. Se puede volver a activar editándola, en «Estado del registro»."
                    />
                </Gate>
            </span>
        </li>
    );
}

export default function TasksOverview() {
    const [params, setParams] = useSearchParams();
    // Turns an id into a name, and falls back to `#id` rather than a blank
    // cell. Gated by the permission the backend enforces on the user list, so
    // somebody without it reads `Usuario #7` instead of firing a 403.
    const { userName } = useAuthorNames();
    const repositoryInfo = useRepositoryInfo();

    const page = Math.max(Number(params.get('page')) || 1, 1);
    const projectId = params.get('project') || '';
    const tierId = params.get('tier') || '';
    const repositoryId = params.get('repo') || '';
    const workflowState = params.get('state') || '';
    const kind = params.get('kind') || '';
    const offset = (page - 1) * TASKS_PAGE_SIZE;

    const { data, isLoading, isFetching, error } = useTasksList({
        limit: TASKS_PAGE_SIZE,
        offset,
        projectId,
        tierId,
        repositoryId,
        workflowState,
        kind,
    });

    // The tier picker narrows with the project: the API filters tiers by
    // project_id, so choosing a project first turns a list of every tier in the
    // system into the handful that belong to it.
    const { data: tierPage } = useTiersList({
        limit: TIER_LOOKUP_LIMIT,
        offset: 0,
        projectId,
    });

    const tierNames = useMemo(() => {
        const map = new Map();
        for (const tier of tierPage?.items ?? []) map.set(tier.id, tier.name);
        return map;
    }, [tierPage]);

    const items = data?.items ?? [];
    const total = data?.pagination?.total ?? 0;
    const lastPage = useMemo(
        () => Math.max(Math.ceil(total / TASKS_PAGE_SIZE), 1),
        [total],
    );

    const setFilter = (key, value) => {
        const target = new URLSearchParams(params);
        if (value) target.set(key, value);
        else target.delete(key);

        // CHANGING THE PROJECT CLEARS THE TIER, because a tier belongs to one
        // project: keeping it would ask for a tier of project A inside project
        // B and come back with nothing. An empty list that looks like "there is
        // no work here" is worse than a filter that resets. A repository
        // belongs to one project too.
        if (key === 'project') {
            target.delete('tier');
            target.delete('repo');
        }

        target.delete('page');
        setParams(target);
    };

    // El cambio entre tablero y lista vive en la pestaña de Desarrollo, que es
    // la que sabe que son dos vistas de la misma consulta. Conservar acá un
    // segundo enlace que hace lo mismo dejaba dos controles para una decisión.

    const goToPage = (next) => {
        const target = new URLSearchParams(params);
        if (next <= 1) target.delete('page');
        else target.set('page', String(next));
        setParams(target);
    };

    const hasFilters = !!(projectId || tierId || repositoryId || workflowState || kind);

    if (isLoading) return <p className="tasks__loading">Cargando tareas...</p>;

    if (error) {
        return (
            <p className="tasks__error">
                {error.message || 'No se pudieron cargar las tareas'}
            </p>
        );
    }

    return (
        <section className="tasks">
            <header className="tasks__header">
                <div className="tasks__heading">
                    <span className="tasks__count">{total} {total === 1 ? 'tarea' : 'tareas'}</span>
                    <h2 className="tasks__title">Tareas</h2>
                </div>

                <div className="tasks__tools">
                    <Gate domain="tasks:read">
                        <Link to="/admin/tasks/trace" className="tasks__view">
                            Buscar por commit
                        </Link>
                    </Gate>

                    <Gate domain="tasks:write">
                        <Link
                            to={`/admin/tasks/new${tierId ? `?tier=${tierId}` : ''}`}
                            className="tasks__new"
                        >
                            + Nueva tarea
                        </Link>
                    </Gate>
                </div>
            </header>

            <div className="tasks__filters">
                <ProjectFilter
                    value={projectId}
                    onChange={(id) => setFilter('project', id)}
                />

                <RepositoryFilter
                    value={repositoryId}
                    projectId={projectId}
                    onChange={(id) => setFilter('repo', id)}
                />

                <Gate domain="tiers:read">
                    <TierFilter projectId={projectId} value={tierId} onChange={value => setFilter('tier', value)} />
                </Gate>

                <select
                    className="tasks__filter"
                    value={workflowState}
                    onChange={e => setFilter('state', e.target.value)}
                    aria-label="Filtrar por estado"
                >
                    <option value="">Todos los estados</option>
                    {WORKFLOW_STATE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </select>

                <select
                    className="tasks__filter"
                    value={kind}
                    onChange={e => setFilter('kind', e.target.value)}
                    aria-label="Filtrar por tipo"
                >
                    <option value="">Todos los tipos</option>
                    {TASK_KIND_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </select>

                {hasFilters && (
                    <button
                        type="button"
                        className="tasks__filter-clear"
                        onClick={() => setParams(new URLSearchParams())}
                    >
                        Limpiar
                    </button>
                )}
            </div>

            {!items.length && (
                <p className="tasks__empty">
                    {hasFilters
                        ? 'Ninguna tarea coincide con estos filtros.'
                        : 'Todavía no hay tareas cargadas.'}
                </p>
            )}

            {!!items.length && (
                <div className={`tasks__list ${isFetching ? 'tasks__list--refreshing' : ''}`}>
                    <div className="task-row task-row--head" aria-hidden="true">
                        <span>Tipo</span>
                        <span>Tarea</span>
                        <span>Estado</span>
                        <span>Responsable</span>
                        <span>Prio</span>
                        <span />
                    </div>

                    <ul className="tasks__rows">
                        {items.map(task => (
                            <TaskRow
                                key={task.id}
                                task={task}
                                tierName={tierNames.get(task.tier_id)}
                                repo={repositoryInfo(task.repository_id)}
                                assignedName={task.assigned_to != null
                                    ? (task.assigned_user?.name ?? userName(task.assigned_to))
                                    : null}
                            />
                        ))}
                    </ul>
                </div>
            )}

            {total > TASKS_PAGE_SIZE && (
                <nav className="tasks__pager">
                    <button
                        type="button"
                        className="tasks__pager-button"
                        onClick={() => goToPage(page - 1)}
                        disabled={page <= 1}
                    >
                        Anterior
                    </button>
                    <span className="tasks__pager-position">Página {page} de {lastPage}</span>
                    <button
                        type="button"
                        className="tasks__pager-button"
                        onClick={() => goToPage(page + 1)}
                        disabled={page >= lastPage}
                    >
                        Siguiente
                    </button>
                </nav>
            )}
        </section>
    );
}
